import React from 'react'
import { CSSTransition } from 'react-transition-group'
import styled from '@emotion/styled'
import { keyframes } from '@emotion/core'
import { withStyles } from '@material-ui/core/styles'
import Modal from '@material-ui/core/Modal'
import Fade from '@material-ui/core/Fade'
import { tablet, mobile, mobileS } from '@/utils/media'
import { rgba, mq } from '@/utils/helpers'
import { easing, layouts } from '@/utils/preset'
import ThemeConsumer from '@/hoc/ThemeConsumer'
import Image from '@/components/common/Image'

class VideoCard extends React.Component {
  constructor (props) {
    super(props)
    this.state = {
      isHover: false,
      isOpen: false
    }
    this.videoRef = React.createRef()
  }
  onMouseEnter = () => {
    this.setState({
      ...this.state,
      isHover: true
    })
    this.videoRef.current.play()
  }
  onMouseLeave = () => {
    this.setState({
      ...this.state,
      isHover: false
    })
    this.videoRef.current.pause()
  }
  onClick = () => {
    this.setState({
      ...this.state,
      isHover: false,
      isOpen: true
    })
  }
  onClose = () => {
    this.setState({
      ...this.state,
      isHover: false,
      isOpen: false
    })
  }
  render () {
    const { classes } = this.props
    return (
      <Root
        onMouseEnter={this.onMouseEnter}
        onMouseLeave={this.onMouseLeave}
      >
        <Wrapper>
          <ThumbnailContainer>
            <Thumbnail fluid={this.props.thumbnail.childImageSharp.fluid} />
          </ThumbnailContainer>
          <CSSTransition
            in={this.state.isHover}
            timeout={300}
          >
            <Video onClick={this.onClick}>
              <Video_Content src={this.props.preview.publicURL} loop="loop" muted="muted" playsInline="playsinline" preload="metadata" ref={this.videoRef} />
            </Video>
          </CSSTransition>
          <Play onClick={this.onClick}>
            <Play_RingContainer className={this.state.isHover ? 'is-hover' : ''}>
              <Play_Ring />
              <Play_Ring />
            </Play_RingContainer>
            <Play_Icon width="1024" height="1024" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
              <path d="M1024 512L256 955.405L256 68.595L1024 512Z" />
            </Play_Icon>
          </Play>
          <Modal
            open={this.state.isOpen}
            onClose={this.onClose}
            onBackdropClick={this.onClose}
            className={classes.root}
            closeAfterTransition={true}
            BackdropProps={{
              classes: {
                root: classes.backdrop
              }
            }}
          >
            <Fade in={this.state.isOpen}>
              <div className={classes.paper}>
                <Close onClick={this.onClose} width="38" height="38" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M25.6155 12.1245C24.8377 11.3467 23.5942 11.3174 22.8522 12.0594L18.8048 16.1068L14.5622 11.8641C13.7844 11.0863 12.5409 11.057 11.7989 11.799C11.0569 12.541 11.0861 13.7845 11.864 14.5624L16.1066 18.805L12.0593 22.8523C11.3173 23.5944 11.3465 24.8379 12.1244 25.6157C12.9022 26.3935 14.1457 26.4228 14.8877 25.6808L18.935 21.6334L23.1777 25.8761C23.9555 26.6539 25.199 26.6832 25.941 25.9412C26.683 25.1992 26.6537 23.9557 25.8759 23.1778L21.6333 18.9352L25.6806 14.8879C26.4226 14.1458 26.3933 12.9024 25.6155 12.1245Z" fill="white"/>
                  <path d="M1 19C1 9.064 9.064 1 19 1C28.936 1 37 9.064 37 19C37 28.936 28.936 37 19 37C9.064 37 1 28.936 1 19Z" stroke="white" strokeWidth="2"/>
                </Close>
                <div className={classes.frame}></div>
                <Embed
                  src={`https://www.youtube.com/embed/${this.props.youtubeId}?rel=0`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen />
              </div>
            </Fade>
          </Modal>
        </Wrapper>
      </Root>
    )
  }
}

const Root = styled.section`
  position: relative;
  padding-top: 42.61715296%;
  overflow: hidden;
  cursor: pointer;
`

const Wrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`

const ThumbnailContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`

const Thumbnail = styled(Image)`
  width: 100%;
  height: 100%;
`

const Video = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transform: scale(1.1);

  &.enter {
    transition:
      opacity 0.3s ease,
      transform 0.3s ease;
  }
  &.enter-active,
  &.enter-done {
    opacity: 1;
    transform: scale(1);
  }
  &.exit {
    transition:
      opacity 0.3s ease,
      transform 0.3s ease;
  }
  &.exit-active,
  &.exit-done {
    opacity: 0;
    transform: scale(1.1);
  }
`

const Video_Content = styled.video`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  min-height: 100%;
  height: auto;
  transform: translate(-50%, -50%);
`

const Play_Icon = styled.svg`
  width: 24px;
  height: 24px;
  fill: #e5e5e5;
  transition: transform 0.3s ease;
  @media ${mq.and(mobile)} {
    width: 14px;
    height: 14px;
  }
`

const anim = keyframes({
  '0%': {
    boxShadow: `0 0 0 2px ${rgba('#e5e5e5', 0)}`
  },
  '40%': {
    boxShadow: `0 0 0 12px ${rgba('#e5e5e5', 0.2)}`
  },
  '70%': {
    boxShadow: `0 0 0 18px ${rgba('#e5e5e5', 0.12)}`
  },
  '100%': {
    boxShadow: `0 0 0 20px ${rgba('#e5e5e5', 0)}`
  },
  label: 'Play'
})

const Play = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  justify-content: center;
  align-items: center;
  width: 80px;
  height: 80px;

  @media ${mq.and(mobile)} {
    width: 42px;
    height: 42px;
  }

  @media (hover: hover), (-ms-high-contrast: none) {
    &:hover {
      ${Play_Icon} {
        transform: scale(1.2);
      }
    }
  }
`

const Play_Ring = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  transition: transform 0.2s ease;
  will-change: box-shadow;
  width: 100%;
  height: 100%;
  border-radius: 50%;

  &:nth-of-type(2) {
    animation-delay: 0.9s;
    border: solid 2px #e5e5e5;
  }
`

const Play_RingContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  &.is-hover {
    ${Play_Ring} {
      animation: ${anim} 2s linear infinite;
      @media (hover: none), ${mq.and(mobile)}, not (-ms-high-contrast: none) {
        animation: none;
      }
    }
  }
`

const Close = styled.svg`
  position: absolute;
  top: -50px;
  right: -50px;
  transition: transform 0.3s ${easing.css.easeInOutCirc};
  cursor: pointer;
  @media (hover: hover), (-ms-high-contrast: none) {
    &:hover {
      transform: scale(1.1);
    }
  }
  @media ${mq.and(tablet)} {
    width: 32px;
    height: 32px;
    top: -48px;
    right: ${layouts.defs.pad.tablet * 2}px;
  }
  @media ${mq.and(mobile)} {
    width: 32px;
    height: 32px;
    top: -48px;
    right: 12.5px;
  }
  @media ${mq.and(mobileS)} {
    width: 32px;
    height: 32px;
    top: -48px;
    right: 10.5px;
  }
`

const Embed = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`

const styles = theme => ({
  root: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    outline: 'none',
    '&:focus': {
      outline: 'none'
    }
  },
  paper: {
    position: 'relative',
    backgroundColor: '#000',
    width: '100%',
    maxWidth: '740px',
    outline: 'none',
    '&:focus': {
      outline: 'none'
    }
  },
  frame: {
    paddingTop: '56.25%',
    outline: 'none',
    '&:focus': {
      outline: 'none'
    }
  },
  video: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    outline: 'none',
    '&:focus': {
      outline: 'none'
    }
  },
  backdrop: {
    backgroundColor: `${rgba('#000', 0.8)}`
  }
})

export default withStyles(styles)(ThemeConsumer(VideoCard))
